import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, Chip, Paper, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import type { GridColDef } from '@mui/x-data-grid';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { PageHeader } from '../../shared/components/PageHeader';
import { MessageDetailDialog } from './MessageDetailDialog';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import type { AiChatMessage, AiChatMessageStatus } from '../../core/types/data-models';

const STATUS_COLORS: Record<AiChatMessageStatus, 'default' | 'info' | 'success' | 'error'> = {
  PENDING: 'default',
  STREAMING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
  CANCELLED: 'default',
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
}

/**
 * One AI Assistant chat session's full message transcript — the backoffice
 * counterpart to the console's own chat sidebar, but read-only and visible
 * across every user. Row click opens `MessageDetailDialog` for the
 * backend-flow detail (composed system prompt, tool calls, tokens).
 */
export function AiChatSessionDetailPage() {
  const params = useParams<{ id: string }>();
  const sessionId = Number(params.id);
  const navigate = useNavigate();
  const [selected, setSelected] = useState<AiChatMessage | null>(null);

  const sessionQuery = useQuery({
    queryKey: ['aiChatMonitoringSessions', sessionId],
    queryFn: () => resources.aiChatMonitoringSessions.get(sessionId),
    enabled: Number.isFinite(sessionId),
  });

  const messagesQuery = useQuery({
    queryKey: ['aiChatMonitoringSessions', sessionId, 'messages'],
    queryFn: () => resources.aiChatMonitoringMessagesFor(sessionId).list({ pageSize: 500 }),
    enabled: Number.isFinite(sessionId),
  });

  const projectQuery = useQuery({
    queryKey: ['projects', sessionQuery.data?.projectId],
    queryFn: () => resources.projects.get(sessionQuery.data!.projectId!),
    enabled: sessionQuery.data?.projectId != null,
  });

  const userQuery = useQuery({
    queryKey: ['users', sessionQuery.data?.userId],
    queryFn: () => resources.users.get(sessionQuery.data!.userId),
    enabled: sessionQuery.data?.userId != null,
  });

  const rolesLookup = useQuery({
    queryKey: ['aiAssistantRoles', 'select'],
    queryFn: () => resources.aiAssistantRoles.list({ pageSize: 200, sort: 'order' }),
  });
  const roleName = useMemo(() => {
    const key = sessionQuery.data?.assistantRoleKey;
    if (!key) return 'General Assistant';
    return rolesLookup.data?.data.find((r) => r.key === key)?.name ?? key;
  }, [sessionQuery.data?.assistantRoleKey, rolesLookup.data]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'createdAt',
        headerName: 'Time',
        width: 170,
        renderCell: (p) => new Date(p.value as string).toLocaleString(),
      },
      {
        field: 'role',
        headerName: 'Role',
        width: 110,
        renderCell: (p) => (
          <Chip label={p.value as string} size="small" color={p.value === 'USER' ? 'default' : 'primary'} variant="outlined" />
        ),
      },
      {
        field: 'content',
        headerName: 'Content',
        flex: 1,
        minWidth: 320,
        renderCell: (p) => truncate((p.value as string) ?? '', 160),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 120,
        renderCell: (p) => (
          <Chip label={p.value as string} size="small" color={STATUS_COLORS[p.value as AiChatMessageStatus]} variant="outlined" />
        ),
      },
      {
        field: 'inputTokens',
        headerName: 'In tokens',
        width: 100,
        type: 'number',
        renderCell: (p) => p.value ?? '—',
      },
      {
        field: 'outputTokens',
        headerName: 'Out tokens',
        width: 100,
        type: 'number',
        renderCell: (p) => p.value ?? '—',
      },
    ],
    [],
  );

  const session = sessionQuery.data;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader
        title={session?.title ?? (session ? 'Untitled chat' : `Session #${sessionId}`)}
        description="Full message transcript for this AI Assistant chat session."
        actions={
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/ai-chat-monitoring')}>
            Back
          </Button>
        }
      />

      {session && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Field label="Created" value={new Date(session.createdAt).toLocaleString()} />
            <Field label="Last activity" value={session.lastMessageAt ? new Date(session.lastMessageAt).toLocaleString() : '—'} />
            <Field label="Project" value={projectQuery.data?.name ?? (session.projectId ? `#${session.projectId}` : '—')} />
            <Field label="Assistant" value={<Chip label={roleName} size="small" color={session.assistantRoleKey ? 'info' : 'default'} variant="outlined" />} />
            <Field label="User" value={userQuery.data?.name ?? userQuery.data?.email ?? `#${session.userId}`} />
          </Stack>
        </Paper>
      )}

      <DataTable<AiChatMessage>
        rows={messagesQuery.data?.data ?? []}
        columns={columns}
        loading={messagesQuery.isFetching}
        rowCount={messagesQuery.data?.data.length ?? 0}
        paginationMode="client"
        errorMessage={messagesQuery.isError ? extractErrorMessage(messagesQuery.error) : undefined}
        onRowClick={(row) => setSelected(row)}
      />

      <MessageDetailDialog open={selected !== null} message={selected} onClose={() => setSelected(null)} />
    </Box>
  );
}
